import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Callout,
  Column,
  FlexBox,
  H3,
  Listbox,
  ListboxButton,
  ListboxList,
  ListboxOption,
  ListboxPopover,
  Row,
  Typography,
  useId,
} from "@vp/swan";
import QuantityDropdownPriceDisplay from "./QuantityDropdownPriceDisplay";
import Quantity from "../../../utils/Quantity";
import { ProductBuilderData } from "../../../types/ProductBuilderTypes";
import { BffDataContext } from "../../../contexts/BffDataContextProvider";
import { ProductContext } from "../../../contexts/ProductContextProvider";
import { PluginDataContext } from "../../../pluginsInfrastructure/PluginDataContextProvider";
import { PageStaticDataContext } from "../../../contexts/PageStaticDataContextProvider";
import AlbelliQtyCookieProvider from "../../../utils/albelliQtyCookieProvider";
import TrackingUtil from "../../../utils/tracking";
import { MerchandisingProductView } from "../../../types/ContentfulTypes";
import { analyticsPageData } from "../../../utils/analyticsConfig";
import { bulkOrderBigQuantity, showBulkOrder } from "../../../utils/bulkOrder";
import TokenEngine from "@vp/vp-js-token-engine";
import "../styles/Selector.scss";
import { whenAvailable } from "@vp/ab-reader";

interface QuantityDropdownProps {
  builderData: ProductBuilderData;
  bulkOrderUrl?: string;
}

const QuantityDropdown = (props: QuantityDropdownProps) => {
  const { bulkOrderUrl, builderData } = props;
  const { compatibleQuantities, temporarilyOutOfStock } = useContext(BffDataContext);
  const { mpv, locale } = useContext(PageStaticDataContext);
  const { selectedQuantity, clientSideStateInitialized, selectQuantity } = useContext(
    ProductContext
  );
  const { pluginData } = useContext(PluginDataContext);
  const [bulkQuantitySelected, setBulkQuantitySelected] = useState(false);

  const titleId = useId();

  const quantityLabel = pluginData.contentful["quantity"];
  const recommended = pluginData.contentful["recommended"];

  const displayQuantityValue = clientSideStateInitialized;
  const recommendedQuantity = Quantity.getRecommendedQuantity(mpv.quantities);

  const getExperimentalOnClickHandler = (props: {
    mpv: MerchandisingProductView;
    pageZone: string;
    pageSection: string;
    pageStage: string;
  }) => {
    const { mpv, pageZone, pageSection, pageStage } = props;
    TrackingUtil.trackExperimentClicked(mpv, {
      label: `${pageZone} view`,
      pageSection,
      pageStage,
      variationName: "control",
      experimentName: "quantity_savings",
    });
  };

  const quantityOnClickHandler = useCallback(
    (_: React.MouseEvent<HTMLElement, MouseEvent>) =>
      getExperimentalOnClickHandler({
        mpv,
        pageZone: "Quantity Dropdown",
        pageSection: analyticsPageData.pageSection,
        pageStage: "Discover",
      }),
    [mpv, getExperimentalOnClickHandler]
  );

  // UseEffect is a Holiday hack, for Albelli Products, save the QTY into a cookie
  useEffect(() => {
    if (!bulkQuantitySelected && selectedQuantity) {
      if (["wallCalendars", "deskCalendars", "photobooks"].includes(mpv.mpvId)) {
        AlbelliQtyCookieProvider.setAlbelliQty(selectedQuantity);
      }
    }
  }, [bulkQuantitySelected, selectedQuantity, mpv.mpvId]);

  if (compatibleQuantities.length <= 0) {
    return <></>;
  }

  const quantityChangeHandler = (newQuantity: string) => {
    if (newQuantity === `${bulkOrderBigQuantity}`) {
      selectQuantity(bulkOrderBigQuantity);
      setBulkQuantitySelected(true);
    } else {
      setBulkQuantitySelected(false);
      selectQuantity(parseInt(newQuantity));
      TrackingUtil.trackBuilderSelection({
        optionKey: "Quantity",
        valueKey: newQuantity,
      });
    }
  };

  const qtyOptions = compatibleQuantities.map((quantity: number) => {
    const isRecommended = quantity === recommendedQuantity;
    const labelNode = (
      <>
        {quantity.toLocaleString(locale)}
        <QuantityDropdownPriceDisplay
          quantity={quantity}
          quantityDropdownPriceDisplay={builderData.quantityDropdownPriceDisplay}
        />
      </>
    );
    return (
      <ListboxOption
        value={quantity}
        key={quantity}
        label={<Typography fontWeight="bold">{labelNode}</Typography>}
      >
        <FlexBox alignItems="center">
          {labelNode}
          {isRecommended && (
            <Callout ml={"3"} skin="announcement" variant="overlay">
              {recommended}
            </Callout>
          )}
        </FlexBox>
      </ListboxOption>
    );
  }) as any;

  if (showBulkOrder(compatibleQuantities, bulkOrderUrl)) {
    const bulkQuantity = compatibleQuantities[compatibleQuantities.length - 1].toLocaleString(
      locale
    );
    const bulkQuantityFormat = pluginData.contentful["bulkOrderQuantity"];
    let labelNode = <Typography fontWeight={"bold"}>{bulkQuantity}</Typography>;
    if (bulkQuantityFormat) {
      const tokenEngine = new TokenEngine();
      labelNode = (
        <Typography fontWeight={"bold"}>
          {tokenEngine.replace(bulkQuantityFormat, {
            quantity: `${bulkQuantity}`,
          })}
        </Typography>
      );
    }

    qtyOptions.push(
      <ListboxOption value={bulkOrderBigQuantity} key={bulkOrderBigQuantity} label={labelNode}>
        <FlexBox alignItems="center">{labelNode}</FlexBox>
      </ListboxOption>
    );
  }

  return (
    <Row className="quantity-row">
      <Column span={12} className="quantity-control">
        <FlexBox flexDirection="column" mb={"3"}>
          <Box mb={"2"}>
            <Typography id={titleId} fontSkin="body-standard-bold" as="label">
              {quantityLabel}
            </Typography>
          </Box>
          <Listbox
            className="quantity-dropdown"
            disabled={temporarilyOutOfStock}
            selectionMode="single"
            value={displayQuantityValue ? selectedQuantity?.toString() || " " : " "}
            onChange={quantityChangeHandler}
            onClick={quantityOnClickHandler}
            fullWidth
          >
            <ListboxButton aria-labelledby={titleId} />
            <ListboxPopover>
              <ListboxList>{qtyOptions}</ListboxList>
            </ListboxPopover>
          </Listbox>
        </FlexBox>
      </Column>
    </Row>
  );
};

export default QuantityDropdown;
